import { useMemo, useState } from 'react';
import { getAuth } from 'firebase/auth';
import { toast } from 'sonner';
import { Check } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { formatVND } from '@/lib/utils';
import Pill from '../../components/kit/Pill';
import { markNotDuplicate } from '../../../ledger/maintenance';
import { suspectedDuplicates, suggestedExtras } from '../overview/actions';
import { toDate, type LedgerEntry } from './entries';

const pad = (n: number) => String(n).padStart(2, '0');
const when = (d: Date | null) => (d ? `${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())} ${pad(d.getDate())}/${pad(d.getMonth() + 1)}` : '—');
const dmy = (d: Date | null) => (d ? `${pad(d.getDate())}/${pad(d.getMonth() + 1)}/${d.getFullYear()}` : '—');

/**
 * Xem các nhóm khoản chi nghi trùng (cùng ngày, số tiền, danh mục, ví): cạnh nhau, kèm giờ tạo, có liên kết với
 * khoản dự kiến hay không, và gợi ý bản nên xoá. Xoá bằng cách bấm vào dòng để mở hộp sửa/xoá; hoặc báo "không phải trùng".
 */
export default function DuplicateReview({ expenses, entries, linkedIds, onOpen, onExit }: {
  expenses: any[]; entries: LedgerEntry[]; linkedIds: Set<string>; onOpen: (e: LedgerEntry) => void; onExit: () => void;
}) {
  const groups = useMemo(() => suspectedDuplicates(expenses), [expenses]);
  const byId = useMemo(() => new Map(entries.map(e => [e.id, e])), [entries]);
  const [busy, setBusy] = useState<string | null>(null);

  const dismiss = async (ids: string[], key: string) => {
    const uid = getAuth().currentUser?.uid; if (!uid) return;
    setBusy(key);
    const r = await markNotDuplicate(uid, ids);
    setBusy(null);
    r.success ? toast.success('Đã ghi nhận không phải trùng — nhóm này sẽ không báo lại') : toast.error(r.error ?? 'Không lưu được');
  };

  return (
    <div className="space-y-4">
      <Card className="flex flex-wrap items-center justify-between gap-3 border-warn/40 bg-warn-soft p-4">
        <p className="text-sm"><b className="text-warn">Đang xem {groups.length} nhóm khoản chi nghi trùng</b> (45 ngày gần nhất). Nếu đúng là trùng, bấm vào dòng nên xoá để xoá; số dư ví sẽ được hoàn lại.</p>
        <Button variant="outline" onClick={onExit}>Xem tất cả giao dịch</Button>
      </Card>

      {groups.length === 0 && (
        <Card className="space-y-2 p-8 text-center"><p className="font-slab text-lg font-semibold">Không còn khoản nghi trùng</p><p className="text-muted-foreground">Các nhóm đã xử lý hoặc đã đánh dấu không phải trùng sẽ không hiện ở đây.</p></Card>
      )}

      {groups.map(g => {
        const first = g[0];
        const ids = g.map(e => e.id);
        const key = ids.sort().join(',');
        const extras = suggestedExtras(g, linkedIds);
        const sorted = [...g].sort((a, b) => (toDate(a.createdAt)?.getTime() ?? 0) - (toDate(b.createdAt)?.getTime() ?? 0));
        return (
          <Card key={key} className="overflow-hidden p-0">
            <div className="flex flex-wrap items-center justify-between gap-2 border-b border-border px-4 py-3">
              <p className="min-w-0 text-sm"><b>{first.category ?? first.categoryName ?? 'Chi'}</b> · <span className="num">{formatVND(Number(first.amount) || 0)}</span> · {dmy(toDate(first.transactionDate))} · {first.walletName ?? 'ví'} · {g.length} bản ghi</p>
              <Button size="sm" variant="outline" disabled={busy === key} onClick={() => dismiss(ids, key)}><Check size={14} aria-hidden /> Không phải trùng</Button>
            </div>
            <ul>
              {sorted.map(r => {
                const e = byId.get(r.id);
                const isLinked = !!r.plannedId || linkedIds.has(r.id);
                return (
                  <li key={r.id} className="perf-top first:border-0">
                    <button type="button" disabled={!e} onClick={() => e && onOpen(e)} className="flex w-full flex-wrap items-center gap-x-3 gap-y-1 px-4 py-3 text-left hover:bg-secondary/60 focus-visible:bg-secondary/60">
                      <span className="min-w-0 flex-1 text-sm">
                        <span className="block truncate font-medium">{r.description || r.note || r.notes || 'Không ghi chú'}</span>
                        <span className="num block text-muted-foreground">Tạo lúc {when(toDate(r.createdAt))}</span>
                      </span>
                      <span className="flex flex-wrap items-center gap-1.5">
                        {isLinked ? <Pill tone="action">Có liên kết dự kiến</Pill> : <Pill tone="neutral">Không liên kết dự kiến</Pill>}
                        {extras.has(r.id) && <Pill tone="bad">Gợi ý xoá bản này</Pill>}
                      </span>
                    </button>
                  </li>
                );
              })}
            </ul>
          </Card>
        );
      })}
    </div>
  );
}
